import { createAsyncThunk } from '@reduxjs/toolkit'
import { saveAs } from 'file-saver'

import { callErrorMsg } from 'helpers/errorMsg'
import { toastFailed, toastSuccess } from 'utils/toast'
import { push } from 'utils/helpers/router'
import { vendor } from 'config/routes'
import { RootStateType } from 'store'
import {
  getVendorDetails,
  getCompanies,
  postVendorRequest,
  postReview,
  getOptions,
  getUoms,
  getPicList,
  getVendorDocumentsFileRulesAPI,
  getDocFileUrlAPI,
  postUploadDocumentsFile,
} from 'features/Enterprise/Vendor/services/addEditVendor'

import {
  type FetchLocationListRequestType,
  getLocationListAPI,
} from 'features/Enterprise/Account/services'
import {
  VendorDetailsInterface,
  VendorDetailRequestInterface,
  VMSFileType,
  VMSDocsIdType,
  VendorSubmissionPayloadType,
} from 'features/Enterprise/Vendor/types/VendorManagement'
import { normalizeVendorDetailsFromApi } from 'features/Enterprise/Vendor/utils/ruleGroupsMapper'

import { getVendorDetailRequest } from '../../services/vendorReview'
import { setVendorSubmissionPayload } from '../VendorReview/vendorReviewSlice'
import { fetchVendorSubmissionData } from '../VendorReview/vendorReviewAction'

import {
  SLICE_NAME,
  setVendorCompany,
  setLoading,
  setVendorDetailRequest,
  setVendorOptions,
  setVendorUoms,
  setAstroPicList,
  setHubLocationList,
  setWhLocationList,
  setFileRules,
  setMegahubList,
  setVendorDetails,
  setShippingLocationList,
} from './addEditVendorSlice'

const draft_seller = 'draft-seller'

// Soon to deprecate. TODO: Remove this thunk and use fetchVendorDetailData instead
export const fetchVendorDetails = createAsyncThunk(
  `${SLICE_NAME}/fetchVendorDetails`,
  async (
    {
      vendor_id,
      handlePrefilledForm,
    }: { vendor_id: string; handlePrefilledForm?: (data: VendorDetailsInterface) => void },
    { dispatch },
  ) => {
    try {
      dispatch(setLoading(true))
      const { data } = await getVendorDetails(vendor_id)

      dispatch(
        setVendorDetailRequest({
          request_data: {
            ...data.data,
            vendor_id: Number(vendor_id),
          },
        } as unknown as VendorDetailRequestInterface),
      )

      if (handlePrefilledForm)
        handlePrefilledForm({
          ...data.data,
          vendor_id: Number(vendor_id),
        })
    } catch (error) {
      callErrorMsg(error)
    } finally {
      dispatch(setLoading(false))
    }
  },
)

export const fetchCompanies = createAsyncThunk(
  `${SLICE_NAME}/fetchCompanies`,
  async (_, { dispatch }) => {
    try {
      const { data } = await getCompanies()
      dispatch(setVendorCompany(data.data))
    } catch (error) {
      callErrorMsg(error)
    }
  },
)

export const submitVendorRequest = createAsyncThunk(
  `${SLICE_NAME}/submitVendorRequest`,
  async (
    {
      payload,
      successMessage,
      redirectTo,
    }: { payload: VendorSubmissionPayloadType; successMessage: string; redirectTo?: string },
    { dispatch },
  ) => {
    try {
      dispatch(setLoading(true))
      await postVendorRequest(payload)
      await fetchVendorDetailData({ vendor_id: payload.vendor_id, dontFetchSubmissionData: false })

      toastSuccess(successMessage)

      if (redirectTo?.length) {
        dispatch(
          push({
            url: `/dashboard/vendor`,
            queryObject: { section: redirectTo },
          }),
        )
      }
    } catch (error) {
      callErrorMsg(error)
      throw error
    } finally {
      dispatch(setLoading(false))
    }
  },
)

// Soon to deprecate. TODO: Remove this thunk and use fetchVendorSubmissionData instead
export const fetchVendorDetailRequest = createAsyncThunk(
  `${SLICE_NAME}/fetchVendorDetailRequest`,
  async (
    {
      vendorId,
      handlePrefilledForm,
    }: { vendorId: string; handlePrefilledForm?: (data: VendorDetailsInterface) => void },
    { dispatch },
  ) => {
    try {
      dispatch(setLoading(true))
      const { data } = await getVendorDetailRequest(vendorId)
      dispatch(setVendorDetailRequest(data?.data as unknown as VendorDetailRequestInterface))
      if (handlePrefilledForm)
        handlePrefilledForm(data?.data?.request_data as never as VendorDetailsInterface)

      if (data.data?.request_data?.vendor_id) {
        dispatch(fetchInvoiceUploadFileRules('seller'))
      } else {
        dispatch(fetchInvoiceUploadFileRules(draft_seller))
      }
    } catch (error) {
      callErrorMsg(error)
    } finally {
      dispatch(setLoading(false))
    }
  },
)

export const submitReview = createAsyncThunk(
  `${SLICE_NAME}/submitReview`,
  async (
    {
      data,
      id,
      url,
      vendorName,
    }: {
      data: { id: number; action: string; reason: string }
      url: string
      id: number | string
      vendorName?: string
    },
    { dispatch },
  ) => {
    try {
      dispatch(setLoading(true))
      await postReview(id, data)
      dispatch(
        push({
          url,
          paramsUrl: { id },
          queryObject: url === vendor ? { section: 'approvals' } : {},
        }),
      )

      let message = `Dokumen ${vendorName} berhasil disetujui`
      if (data.action === 'REJECTED') message = `Dokumen ${vendorName} Ditolak-Menunggu Revisi`
      else if (data.action === 'CANCELLED') message = `Dokumen ${vendorName} dibatalkan`

      if (data.action !== 'REVIEWED') toastSuccess(message)
    } catch (error) {
      callErrorMsg(error)
    } finally {
      dispatch(setLoading(false))
    }
  },
)

export const fetchOptions = createAsyncThunk(
  `${SLICE_NAME}/fetchOptions`,
  async (_, { dispatch }) => {
    try {
      const { data } = await getOptions()
      dispatch(setVendorOptions(data?.data?.options || []))
    } catch (error) {
      callErrorMsg(error)
    }
  },
)

export const fetchUoms = createAsyncThunk(`${SLICE_NAME}/fetchUom`, async (_, { dispatch }) => {
  try {
    const { data } = await getUoms({ isShown: true })
    dispatch(setVendorUoms(data?.data || []))
  } catch (error) {
    callErrorMsg(error)
  }
})

export const getchPicList = createAsyncThunk(
  `${SLICE_NAME}/fetchUom`,
  async (name: string, { dispatch }) => {
    try {
      const { data } = await getPicList(name)
      dispatch(
        setAstroPicList(
          data?.data?.users?.map((user) => ({
            id: 0,
            user_id: user.id,
            email: user.email,
            name: user.full_name || '',
            phone_number: user.phone_number,
          })) || [],
        ),
      )
    } catch (error) {
      callErrorMsg(error)
    }
  },
)

export const fetchLocations = createAsyncThunk(
  `${SLICE_NAME}/fetchLocations`,
  async (query: FetchLocationListRequestType, { dispatch }) => {
    try {
      const { data } = await getLocationListAPI(query)
      if (query?.type === 'HUB') dispatch(setHubLocationList(data.content))
      else if (query?.type === 'MEGAHUB 2.0') dispatch(setMegahubList(data.content))
      else dispatch(setWhLocationList(data.content))
    } catch (error) {
      callErrorMsg(error)
    }
  },
)

export const fetchInvoiceUploadFileRules = createAsyncThunk(
  `${SLICE_NAME}/fetchInvoiceUploadFileRules`,
  async (typeId: VMSDocsIdType, { dispatch }) => {
    try {
      const { data } = await getVendorDocumentsFileRulesAPI(typeId)
      dispatch(setFileRules(data.data))
    } catch (error) {
      callErrorMsg(error)
      throw error
    }
  },
)

export const fetchDocFileUrl = createAsyncThunk(
  `${SLICE_NAME}/fetchDocFileUrl`,
  async (
    {
      file: { filePath, fileName, extension },
      isDowmload,
      isSeller,
    }: {
      file: { filePath: string; fileName: string; extension: string }
      isDowmload?: boolean
      isSeller: boolean
    },
    { dispatch },
  ) => {
    try {
      dispatch(setLoading(true))
      const mapFileType = {
        pdf: 'application/pdf',
        jpg: 'image/jpeg',
        jpeg: 'image/jpeg',
        png: 'image/png',
      }

      const typeId = isSeller ? 'seller' : draft_seller
      const { data } = await getDocFileUrlAPI(
        filePath,
        mapFileType[extension as keyof typeof mapFileType] || 'application/octet-stream',
        typeId,
      )

      if (isDowmload) {
        saveAs(data, fileName)
      }

      return URL.createObjectURL(
        new Blob([data], {
          type: mapFileType[extension as keyof typeof mapFileType] || 'application/octet-stream',
        }),
      )
    } catch (error) {
      callErrorMsg(error)
      throw error
    } finally {
      dispatch(setLoading(false))
    }
  },
)

export const uploadVMSDocs = createAsyncThunk(
  `${SLICE_NAME}/uploadVMSDocs`,
  async (
    {
      file_input,
      vendor_id,
      onUploadSuccess,
    }: {
      file_input: File
      vendor_id?: number
      onUploadSuccess: (fileData: VMSFileType) => void
    },
    { dispatch, getState },
  ) => {
    const {
      auth: {
        userData: { id },
      },
    } = getState() as RootStateType
    const data: { file_input: File; created_by: string; external_id?: number } = {
      file_input,
      created_by: String(id),
    }
    if (vendor_id) data.external_id = vendor_id

    dispatch(setLoading(true))
    postUploadDocumentsFile([
      {
        typeId: vendor_id ? 'seller' : draft_seller,
        data,
      },
    ])
      .then((resp) => {
        if (resp[0].error) {
          toastFailed(resp[0].message)
        } else {
          onUploadSuccess({
            file_path: resp[0].files?.filePath,
            file_url: resp[0].files?.fileUrl,
            file_name: resp[0].files?.fileName,
          })
        }
      })
      .catch(() => {
        toastFailed('Terjadi kesalahan saat mengupload file')
      })
      .finally(() => {
        dispatch(setLoading(false))
      })
  },
)

export const fetchVendorDetailData = createAsyncThunk(
  `${SLICE_NAME}/fetchVendorDetailData`,
  async (
    {
      vendor_id,
      dontFetchSubmissionData,
    }: { vendor_id: number; dontFetchSubmissionData?: boolean },
    { dispatch, getState },
  ) => {
    try {
      const {
        auth: { userData },
      } = getState() as RootStateType
      const { data } = await getVendorDetails(vendor_id.toString())
      const normalizedDetails = normalizeVendorDetailsFromApi({
        ...data.data,
        vendor_id,
      } as VendorDetailsInterface)
      dispatch(setVendorDetails(normalizedDetails))

      if (data?.data?.submission?.id && !dontFetchSubmissionData) {
        dispatch(fetchVendorSubmissionData(data?.data?.submission?.id))
      } else {
        dispatch(
          setVendorSubmissionPayload({
            data: normalizedDetails,
            user: userData?.email,
            submission_id: data?.data?.submission?.id || 0,
          }),
        )
      }
    } catch (error) {
      callErrorMsg(error)
    }
  },
)

export const fetchShippingInfoLocation = createAsyncThunk(
  `${SLICE_NAME}/fetchShippingInfoLocation`,
  async ({ locationName }: { locationName?: string }, { dispatch }) => {
    try {
      const availableLocationType = ['HUB', 'WAREHOUSE', 'MEGAHUB 2.0']
      const [hubResponse, whResponse, megahubResponse] = await Promise.all(
        availableLocationType.map((type) =>
          getLocationListAPI({ type, name: locationName || '', pageSize: '999' }),
        ),
      )
      dispatch(
        setShippingLocationList(
          [...hubResponse.data.content, ...whResponse.data.content, ...megahubResponse.data.content]
            ?.map((loc) => ({
              location_id: loc.location_id,
              location_name: loc.location_name,
              location_type: loc.location_type,
              location_active: loc.location_active,
            }))
            ?.filter((loc) => loc.location_active),
        ),
      )
      // dispatch(setShippingLocationList([...hubResponse.data.content, ...whResponse.data.content]))
    } catch (error) {
      callErrorMsg(error)
    }
  },
)
